import type { SelectedSystemType } from 'data/types';
import {
  getZoneDuctedCoverage,
  type DuctedCoverageType
} from 'utils/ducted-coverage';
import { zoneHeatingCapacity } from 'utils/ducted-zone-unit-rows';
import type { ExistingEquipmentSizeType } from 'utils/existing-equipment-size';
import {
  classifyZoneMatch,
  type ZoneMatchLabel,
  type ZoneMatchVariant,
  type ZoneSizingThresholds
} from 'utils/zone-match-labels';
import { buildZoneMatchSideRules } from 'utils/zone-match-side-rules';

type SystemCondenser =
  SelectedSystemType['configuration']['condensers'][number];
type SystemZone = SystemCondenser['zones'][number];

type DuctedPerformanceZone = SystemZone & {
  blowerProduct?: SystemZone['selectedProduct'];
  sizingThresholds?: ZoneSizingThresholds;
};

export type DuctedZonePerformanceRow = {
  condenser: SystemCondenser;
  zone: DuctedPerformanceZone;
  zoneIndex: number;
  totalHeatingRequired: number;
  totalCoolingRequired: number;
  heatingCapacity: number;
  coolingCapacity: number;
  heatingMatchPercent: number;
  coolingMatchPercent: number;
  matchLabel: ZoneMatchLabel;
  matchVariant: ZoneMatchVariant;
  spaceLabels: string[];
};

const UNKNOWN_MATCH = { label: '—' as const, variant: 'unknown' as const };

function requirementBtu(value: number | null | undefined): number {
  if (value == null || !Number.isFinite(value) || value <= 0) return 0;
  return value;
}

export function summariseRequiredBTUs(
  zone: SystemZone,
  ductedCoverage?: DuctedCoverageType | null
): { heating: number; cooling: number } {
  const fromItems = zone.zoneItems.reduce(
    (acc, item) => ({
      heating: acc.heating + (item.BTUs?.heating ?? 0),
      cooling: acc.cooling + (item.BTUs?.cooling ?? 0)
    }),
    { heating: 0, cooling: 0 }
  );
  const coverage = getZoneDuctedCoverage(zone) ?? ductedCoverage;
  return {
    heating:
      fromItems.heating > 0
        ? fromItems.heating
        : requirementBtu(coverage?.heatingRequirementBtu),
    cooling:
      fromItems.cooling > 0
        ? fromItems.cooling
        : requirementBtu(coverage?.coolingRequirementBtu)
  };
}

export function formatRequiredCapacity(
  heating: number,
  cooling: number
): string {
  if (heating <= 0 && cooling <= 0) {
    return '—';
  }
  const format = (value: number) =>
    new Intl.NumberFormat('en-US').format(value);
  return `${format(heating)} / ${format(cooling)} BTU`;
}

export function formatBtuOutput(value: number): string {
  if (value <= 0) return '—';
  return `${new Intl.NumberFormat('en-US').format(value)} BTU`;
}

export function buildDuctedZonePerformance(
  condensers: SystemCondenser[],
  ductedCoverage?: DuctedCoverageType | null,
  existingEquipmentSize?: ExistingEquipmentSizeType | null
): DuctedZonePerformanceRow[] {
  const rows: DuctedZonePerformanceRow[] = [];
  let zoneIndex = 0;
  condensers.forEach((condenser) => {
    condenser.zones.forEach((zone) => {
      const performanceZone = zone as DuctedPerformanceZone;
      const required = summariseRequiredBTUs(performanceZone, ductedCoverage);
      const heatingCapacity = zoneHeatingCapacity(performanceZone);
      const coolingCapacity = performanceZone.selectedProduct?.coolingBTU ?? 0;
      const hasRequired = required.heating > 0 || required.cooling > 0;
      // Uncapped: a 216% furnace has to read as 216%, or the label can
      // never tell it from one just past the band.
      const heatingMatchPercent =
        required.heating > 0 ? (heatingCapacity / required.heating) * 100 : 100;
      const coolingMatchPercent =
        required.cooling > 0 ? (coolingCapacity / required.cooling) * 100 : 100;
      const isDucted = performanceZone.type === 'ducted';
      const sideRules = buildZoneMatchSideRules(
        performanceZone,
        { heating: heatingCapacity, cooling: coolingCapacity },
        isDucted ? existingEquipmentSize : undefined,
        condenser.selectedProduct
      );
      const match =
        (hasRequired
          ? classifyZoneMatch(
              heatingMatchPercent,
              coolingMatchPercent,
              isDucted ? performanceZone.sizingThresholds : undefined,
              sideRules
            )
          : null) ?? UNKNOWN_MATCH;
      const spaceLabels = performanceZone.zoneItems
        .map((item) => item.name)
        .filter(Boolean);
      rows.push({
        condenser,
        zone: performanceZone,
        zoneIndex,
        totalHeatingRequired: required.heating,
        totalCoolingRequired: required.cooling,
        heatingCapacity,
        coolingCapacity,
        heatingMatchPercent: Math.round(heatingMatchPercent),
        coolingMatchPercent: Math.round(coolingMatchPercent),
        matchLabel: match.label,
        matchVariant: match.variant,
        spaceLabels
      });
      zoneIndex += 1;
    });
  });
  return rows;
}
